<template>
  <main dir="rtl" class="flex-1 pb-8">
    <div class="mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-2xl font-bold text-gray-900">إدارة باقات الاشتراك</h2>
        <button @click="loadPackages" class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg">تحديث</button>
      </div>

      <div v-if="errorMessage" class="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700">
        {{ errorMessage }}
      </div>

      <div class="bg-white rounded-lg shadow p-4 mb-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-3">إضافة / تعديل باقة</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <input v-model="form.name" type="text" class="w-full p-2 border border-gray-300 rounded-md" placeholder="اسم الباقة" />
          <input v-model.number="form.duration_days" type="number" min="1" class="w-full p-2 border border-gray-300 rounded-md" placeholder="المدة بالأيام" />
          <input v-model.number="form.price" type="number" min="0" step="0.01" class="w-full p-2 border border-gray-300 rounded-md" placeholder="السعر" />
          <input v-model="form.currency" type="text" maxlength="3" class="w-full p-2 border border-gray-300 rounded-md" placeholder="SAR" />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <label class="flex items-center gap-2 text-sm text-gray-700">
            <input v-model="form.is_free" type="checkbox" class="rounded border-gray-300" />
            مجانية
          </label>
          <label class="flex items-center gap-2 text-sm text-gray-700">
            <input v-model="form.is_active" type="checkbox" class="rounded border-gray-300" />
            مفعّلة
          </label>
        </div>
        <textarea v-model="form.description" rows="2" class="w-full mt-3 p-2 border border-gray-300 rounded-md" placeholder="وصف الباقة (اختياري)" />
        <div class="mt-3 flex items-center gap-2">
          <button @click="submitForm" class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md">
            {{ form.id ? 'تحديث الباقة' : 'إضافة باقة' }}
          </button>
          <button v-if="form.id" @click="resetForm" class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md">إلغاء التعديل</button>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow overflow-x-auto">
        <table class="min-w-full">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الاسم</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">المدة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">السعر</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">النوع</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">الحالة</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500">إجراءات</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-for="pkg in packages" :key="pkg.id">
              <td class="px-4 py-3 text-sm text-center">{{ pkg.name }}</td>
              <td class="px-4 py-3 text-sm text-center">{{ pkg.duration_days }} يوم</td>
              <td class="px-4 py-3 text-sm text-center">{{ Number(pkg.price || 0).toFixed(2) }} {{ pkg.currency || 'SAR' }}</td>
              <td class="px-4 py-3 text-sm text-center">{{ pkg.is_free ? 'مجانية' : 'مدفوعة' }}</td>
              <td class="px-4 py-3 text-sm text-center">
                <span :class="pkg.is_active ? 'text-green-700 bg-green-50' : 'text-gray-700 bg-gray-100'" class="px-2 py-1 rounded text-xs">
                  {{ pkg.is_active ? 'مفعّلة' : 'غير مفعّلة' }}
                </span>
              </td>
              <td class="px-4 py-3 text-sm text-center">
                <div class="flex items-center justify-center gap-2">
                  <button @click="editPackage(pkg)" class="bg-blue-50 text-blue-700 hover:bg-blue-100 px-3 py-1 rounded">تعديل</button>
                  <button @click="toggleStatus(pkg)" class="bg-amber-50 text-amber-700 hover:bg-amber-100 px-3 py-1 rounded">
                    {{ pkg.is_active ? 'تعطيل' : 'تفعيل' }}
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!packages.length">
              <td colspan="6" class="px-4 py-6 text-center text-gray-500">لا توجد باقات حالياً</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </main>
</template>

<script>
import { defineComponent } from 'vue';
import {
  createIqraSubscriptionPackage,
  listIqraSubscriptionPackages,
  toggleIqraSubscriptionPackageStatus,
  updateIqraSubscriptionPackage,
} from '@/services/iqraSchedules';

export default defineComponent({
  name: 'IqraSubscriptionPackagesManagement',
  data() {
    return {
      loading: false,
      errorMessage: '',
      packages: [],
      form: this.emptyForm(),
    };
  },
  async mounted() {
    await this.loadPackages();
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        name: '',
        description: '',
        duration_days: 30,
        price: 0,
        currency: 'SAR',
        is_free: false,
        is_active: true,
      };
    },
    resetForm() {
      this.form = this.emptyForm();
    },
    async loadPackages() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const result = await listIqraSubscriptionPackages({ page: 1, size: 200 });
        this.packages = result?.data || [];
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحميل الباقات';
      } finally {
        this.loading = false;
      }
    },
    editPackage(pkg) {
      this.form = {
        id: pkg.id,
        name: pkg.name || '',
        description: pkg.description || '',
        duration_days: Number(pkg.duration_days || 30),
        price: Number(pkg.price || 0),
        currency: pkg.currency || 'SAR',
        is_free: !!pkg.is_free,
        is_active: !!pkg.is_active,
      };
    },
    async submitForm() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const payload = {
          name: this.form.name,
          description: this.form.description || null,
          duration_days: this.form.duration_days,
          price: this.form.is_free ? 0 : this.form.price,
          currency: (this.form.currency || 'SAR').toUpperCase(),
          is_free: !!this.form.is_free,
          is_active: !!this.form.is_active,
        };
        if (this.form.id) {
          await updateIqraSubscriptionPackage(this.form.id, payload);
        } else {
          await createIqraSubscriptionPackage(payload);
        }
        this.resetForm();
        await this.loadPackages();
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في حفظ الباقة';
      } finally {
        this.loading = false;
      }
    },
    async toggleStatus(pkg) {
      this.loading = true;
      this.errorMessage = '';
      try {
        await toggleIqraSubscriptionPackageStatus(pkg.id, !pkg.is_active);
        await this.loadPackages();
      } catch (error) {
        this.errorMessage = error?.message || 'فشل في تحديث حالة الباقة';
      } finally {
        this.loading = false;
      }
    },
  },
});
</script>
